import React from 'react';

import { DuplicateIcon } from '@vlabs/icons';
import { useModal } from '@vlabs/uikit';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button';
import type { IconButtonProps } from '@vlabs/uikit/components/controls/button/icon-button';
import { routes } from '@vlapps/point/src/app/routes';
import { useHistory, generatePath } from 'react-router-dom';
import { toast } from 'react-toastify';

import { RoleForm } from '@vlabs/monokit/entities/roles';
import type { CreateDefaultValues } from '@vlabs/monokit/entities/roles';
import { useGetRole, useCreateRole } from '@vlabs/api/artemy/client';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { useDuplicateRoleTranslation } from './i18n';
import type { DuplicateRole } from './types';

type DuplicateRoleIconButtonProps = DuplicateRole & IconButtonProps;

const displayName = 'DuplicateRoleIconButton';
const testid = buildTestid(displayName);

function DuplicateRoleIconButton({
  roleId,
  'data-testid': dataTestId = undefined,
  ...props
}: DuplicateRoleIconButtonProps): React.JSX.Element {
  const { t } = useDuplicateRoleTranslation();
  const history = useHistory();
  const modal = useModal({ size: 'small' });

  const { data: role } = useGetRole(roleId);
  const createRole = useCreateRole({
    mutation: {
      onSuccess: (data) => {
        toast.success(t('toast_success'));
        history.push(generatePath(routes.roles.view, { roleId: data.role_id }));
        modal.close();
      },
      onError: () => {
        toast.error(t('toast_error'));
      },
    },
  });

  const defaultValues = {
    name: `Copy_of_${role?.name}`,
    description: role?.description ? `Copy_of_${role.description}` : undefined,
  };

  const handleSubmit = async (values: CreateDefaultValues): Promise<void> => {
    await createRole.mutateAsync({ data: { ...values, permissions: role?.permissions } });
  };

  return (
    <React.Fragment>
      {modal.wrap(
        <RoleForm
          context="create"
          defaultValues={defaultValues}
          onCancel={modal.close}
          onSubmit={handleSubmit}
        />,
      )}

      <IconButton
        icon={<DuplicateIcon />}
        kind="secondary"
        onClick={modal.open}
        variant="flat"
        {...testid(dataTestId)}
        {...props}
      />
    </React.Fragment>
  );
}

DuplicateRoleIconButton.displayName = displayName;

export { DuplicateRoleIconButton };
